import React from "react";

import classNames from "classnames";
import { CustomHeader, SidebarHeader, TimelineHeaders } from "okast-timeline";

import "./timeline.scss";
import { TIME_FORMAT, TIME_FORMAT_SHORT } from "../../../constants/dateFormat";

import type { Moment } from "moment";
import type { CSSProperties, JSX, MutableRefObject } from "react";

import type { TimelineRef } from "./timeline.types";

export const SIDEBAR_SIZE = 30;

const HEADER_SIZE = 50;
const WIDTH_TEXT_FORMAT_HH_MM_SS = 60;
const WIDTH_TEXT_FORMAT_HH_MM = 40;

const formatDate = (date: Moment, unit: string): string => {
  switch (unit) {
    case "second":
      return date.format(TIME_FORMAT);
    case "minute":
      return date.format(TIME_FORMAT);
    default:
      return date.format(TIME_FORMAT_SHORT);
  }
};

const getScales = (width: number, unit: string): JSX.Element[] => {
  const numberOfScale = (numberScale: number): JSX.Element[] => {
    const scales = [];
    for (let i = 0; i < numberScale; i += 1) {
      scales.push(<div className="flex-auto" />);
    }
    return scales.map((scale) => scale);
  };

  switch (unit) {
    case "second":
      return numberOfScale(0);
    case "minute":
      if (width > WIDTH_TEXT_FORMAT_HH_MM_SS) {
        return numberOfScale(2);
      }
      return numberOfScale(0);
    default:
      return numberOfScale(6);
  }
};

const showTimeValue = (date: Moment, width: number, unit: string): boolean => {
  if (unit === "hour") {
    const hours = parseInt(date.format("HH"), 10);
    if (width > WIDTH_TEXT_FORMAT_HH_MM) {
      return true;
    }
    if (width > WIDTH_TEXT_FORMAT_HH_MM * (3 / 4)) {
      return hours % 2 === 0;
    }
    return hours % 4 === 0;
  }
  if (unit === "minute") {
    const minutes = parseInt(date.format("mm"), 10);
    if (width > WIDTH_TEXT_FORMAT_HH_MM_SS) {
      return true;
    }
    if (width > WIDTH_TEXT_FORMAT_HH_MM_SS * (3 / 4)) {
      return minutes % 2 === 0;
    }
    if (width > WIDTH_TEXT_FORMAT_HH_MM_SS * (4 / 10)) {
      return minutes % 5 === 0;
    }
    return minutes % 10 === 0;
  }
  if (unit === "second") {
    const seconds = parseInt(date.format("ss"), 10);
    return seconds % 10 === 0;
  }
  return true;
};

interface IntervalProps {
  style: CSSProperties;
  unit: string;
  width: number;
  startTime: Moment;
  isVerticalTimeline?: boolean;
}

function Interval(props: IntervalProps): JSX.Element {
  const { unit, width, style, startTime, isVerticalTimeline } = props;

  const isShowTime =
    isVerticalTimeline || showTimeValue(startTime, width, unit);
  const dateText = formatDate(startTime, unit);

  const classGraduationContainer = classNames(
    "flex shrink-0 grow-0 ",
    // unit !== "hour" && !isShowTime ? "basis-1/4" : "basis-2/5",
    { "basis-1/4": unit !== "hour" && !isShowTime && !isVerticalTimeline },
    { "basis-2/5": (unit === "hour" || isShowTime) && !isVerticalTimeline },
    { "basis-1/5": isVerticalTimeline },
    { "border-l-2": isShowTime && !isVerticalTimeline },
    { "border-l": !isShowTime && !isVerticalTimeline },

    isVerticalTimeline
      ? "flex-row border-r-2 border-r-border border-y-border border-t-2"
      : "flex-col border-b-2 border-y-border border-x-border",
  );

  const classTextContainer = classNames(
    "flex shrink-0 grow-0 ",
    // unit !== "hour" && !isShowTime ? "basis-3/4" : "basis-3/5",
    { "basis-3/4": unit !== "hour" && !isShowTime && !isVerticalTimeline },
    { "basis-3/5": (unit === "hour" || isShowTime) && !isVerticalTimeline },
    { "basis-4/5": isVerticalTimeline },
    isVerticalTimeline ? "justify-center items-start" : "items-center",
  );

  const wrapperStyle = classNames(
    "flex bg-transparent text-foreground",
    isVerticalTimeline ? "w-full flex-row" : "h-full flex-col ",
  );

  const timeClassNames = classNames(
    "relative",
    isVerticalTimeline ? "-translate-y-1/2" : "-translate-x-1/2",
  );

  const scaleContainerClassNames = classNames(
    "flex shrink-0 grow-0 basis-3/5 [&>div]:border-border",
    isVerticalTimeline
      ? "flex-col [&>div:first-child]:border-t-0 [&>div:last-child]:border-b-0 [&>div]:border-t"
      : "flex-row [&>div:first-child]:border-l-0 [&>div:last-child]:border-r-0 [&>div]:border-l",
  );

  return (
    <div className={wrapperStyle} style={style}>
      <div className={classTextContainer}>
        {isShowTime ? <div className={timeClassNames}>{dateText}</div> : null}
      </div>
      <div className={classGraduationContainer}>
        <div className="flex flex-auto" />
        <div className={scaleContainerClassNames}>{getScales(width, unit)}</div>
      </div>
    </div>
  );
}

interface ITimelineHeader {
  sidebarSize?: number;
  timelineRef?: MutableRefObject<TimelineRef | null>;
  headerSize?: number;
}

function TimelineHeader({
  sidebarSize = SIDEBAR_SIZE,
  timelineRef,
  headerSize = HEADER_SIZE,
}: ITimelineHeader): JSX.Element {
  const isVerticalTimeline = timelineRef?.current?.props.isVertical;

  return (
    <TimelineHeaders>
      <SidebarHeader>
        {(): JSX.Element => (
          <div
            className="bg-surface-muted"
            style={{
              height: isVerticalTimeline ? `${sidebarSize}px` : "",
              width: isVerticalTimeline ? "100%" : `${sidebarSize}px`,
            }}
          />
        )}
      </SidebarHeader>
      <CustomHeader size={headerSize}>
        {(props): React.ReactNode => {
          if (!props) return null;

          const { headerContext, getRootProps, getIntervalProps } = props;

          const { unit } = headerContext;

          const { style } = getRootProps();

          return (
            <div style={style}>
              {headerContext.intervals.map((interval) => {
                const { startTime, labelSize, endTime } = interval;
                const { style: intervalStyle, key } = getIntervalProps({
                  interval: {
                    ...interval,
                    startTime: startTime.valueOf(),
                    endTime: endTime.valueOf(),
                  },
                });

                return (
                  <Interval
                    key={key}
                    isVerticalTimeline={isVerticalTimeline}
                    startTime={startTime}
                    style={intervalStyle}
                    unit={unit}
                    width={labelSize}
                  />
                );
              })}
            </div>
          );
        }}
      </CustomHeader>
    </TimelineHeaders>
  );
}

TimelineHeader.secretKey = "TimelineHeaders";

export default TimelineHeader;
